import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { UserType } from "@/constants";
import { getRegistrantAccountOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import type {
  LicenseResponseSchema,
  RegistrantQuestionsResponseSchema,
} from "@/hera-api/client/types.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import {
  answerOptionsToSelectOptions,
  getFirstAnswerOptionKey,
} from "@/lib/answer-option-order";
import { accountDataForNacRegistrantCopy } from "@/lib/license-nac-account-data";
import { isNacStyleAnswerType } from "@/lib/nac-answer-types";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import {
  QuestionField,
  companyCopyFieldProps,
  validatorForQuestionType,
} from "../../registrant/form-utils/question-field-utils";

interface EditAdditionalInfoDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  data: LicenseResponseSchema;
  additionalQuestions?: RegistrantQuestionsResponseSchema;
  onSave?: (answers: Record<string, any>) => Promise<void>;
  userType: UserType;
  elevated?: boolean;
}

export function EditAdditionalInfoDialog({
  isOpen,
  onOpenChange,
  data,
  additionalQuestions,
  onSave,
  userType,
  elevated = false,
}: EditAdditionalInfoDialogProps) {
  const { isAuthSetupComplete } = useAuthQuerySetup();

  const { data: registrantApiAccount } = useQuery({
    ...getRegistrantAccountOptions(),
    enabled: isAuthSetupComplete && userType === UserType.REGISTRANT,
  });

  const nacCopyAccountData = useMemo(
    () =>
      accountDataForNacRegistrantCopy(
        userType,
        data.registrant_account,
        registrantApiAccount,
      ),
    [userType, data.registrant_account, registrantApiAccount],
  );

  const [answers, setAnswers] = useState(
    data.answers_to_additional_questions || {},
  );
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [forceValidation, setForceValidation] = useState(false);

  // Only include registration questions in the edit form
  const registrationQuestions = useMemo(
    () =>
      additionalQuestions?.questions.filter(
        (q) =>
          q.question_type === "registration" &&
          q.answer_type !== "information" &&
          !isNacStyleAnswerType(q.answer_type),
      ) ?? [],
    [additionalQuestions?.questions],
  );

  // Create validation schema based on registration questions only
  const validationSchema = useMemo(() => {
    if (registrationQuestions.length === 0) return z.object({});

    const schemaFields: Record<string, z.ZodTypeAny> = {};

    for (const question of registrationQuestions) {
      schemaFields[question.id] = validatorForQuestionType(
        question.answer_type,
        question.answer_type === "boolean" ||
          question.answer_type === "single_select" ||
          (question.answer_required as boolean),
        isNacStyleAnswerType(question.answer_type)
          ? ((question as { select_from_fixed_list?: boolean })
              .select_from_fixed_list ?? false)
          : undefined,
        undefined,
        question.allow_multiple_urls ?? false,
        question.allow_multiple_documents ?? false,
      );
    }

    return z.object(schemaFields);
  }, [registrationQuestions]);

  // Reset answers when dialog opens or when data changes
  useEffect(() => {
    if (isOpen) {
      const originalAnswers = data.answers_to_additional_questions || {};

      // Initialize defaults for unanswered single-select fields
      const initializedAnswers = { ...originalAnswers };

      if (registrationQuestions.length > 0) {
        for (const question of registrationQuestions) {
          if (
            question.answer_type === "single_select" &&
            question.answer_options &&
            Object.keys(question.answer_options).length > 0
          ) {
            const existing = initializedAnswers[question.id];
            const missing =
              !(question.id in initializedAnswers) ||
              existing === null ||
              existing === undefined ||
              existing === "";
            if (missing) {
              const first = getFirstAnswerOptionKey(
                question.answer_options,
                question.answer_option_keys_order,
              );
              if (first !== undefined) {
                initializedAnswers[question.id] = first;
              }
            }
          }
          // Boolean: leave unset so Yes/No radios start with nothing selected
        }
      }

      setAnswers(initializedAnswers);
      setValidationErrors({});
      setForceValidation(false);
    }
  }, [isOpen, data.answers_to_additional_questions, registrationQuestions]);

  // Validate answers whenever they change
  useEffect(() => {
    if (Object.keys(answers).length === 0) {
      setValidationErrors({});
      return;
    }

    const result = validationSchema.safeParse(answers);
    if (!result.success) {
      const errors: Record<string, string> = {};
      for (const error of result.error.errors) {
        if (error.path[0]) {
          errors[error.path[0] as string] = error.message;
        }
      }
      setValidationErrors(errors);
    } else {
      setValidationErrors({});
    }
  }, [answers, validationSchema]);

  const handleSave = async () => {
    // Validate before saving
    const result = validationSchema.safeParse(answers);
    if (!result.success) {
      const errors: Record<string, string> = {};
      for (const error of result.error.errors) {
        if (error.path[0]) {
          errors[error.path[0] as string] = error.message;
        }
      }
      setValidationErrors(errors);
      setForceValidation(true);
      return;
    }

    if (onSave) {
      await onSave(answers);
      onOpenChange(false);
    }
  };

  const handleFieldChange = (questionId: string, value: any) => {
    const newAnswers = {
      ...answers,
      [questionId]: value,
    };
    setAnswers(newAnswers);

    // Validate immediately after field change
    if (Object.keys(newAnswers).length > 0) {
      const result = validationSchema.safeParse(newAnswers);
      if (!result.success) {
        const errors: Record<string, string> = {};
        for (const error of result.error.errors) {
          if (error.path[0]) {
            errors[error.path[0] as string] = error.message;
          }
        }
        setValidationErrors(errors);
      } else {
        setValidationErrors({});
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        elevated={elevated}
        className="max-w-2xl max-h-[80vh] overflow-y-auto"
        key={`edit-additional-info-${data.id}`}
      >
        <DialogHeader>
          <DialogTitle>Edit Additional Information</DialogTitle>
          <DialogDescription>
            Update the additional information for this registration.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {registrationQuestions.map((question) => {
            const field = {
              state: {
                value: answers[question.id],
                errors: validationErrors[question.id]
                  ? [validationErrors[question.id]]
                  : undefined,
              },
              handleChange: (value: any) =>
                handleFieldChange(question.id, value),
              handleBlur: () => {},
            };

            return (
              <div key={question.id} className="space-y-2">
                <label
                  htmlFor={`question-${question.id}`}
                  className="text-base font-semibold"
                >
                  {question.question}
                  {question.answer_required && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
                <QuestionField
                  answerType={question.answer_type}
                  allowMultipleUrls={question.allow_multiple_urls ?? false}
                  allowMultipleDocuments={
                    question.allow_multiple_documents ?? false
                  }
                  field={field}
                  answerOptions={
                    question.answer_options
                      ? answerOptionsToSelectOptions(
                          question.answer_options,
                          question.answer_option_keys_order,
                        )
                      : undefined
                  }
                  questionName={question.question}
                  accountData={nacCopyAccountData}
                  userType={userType}
                  forceValidation={forceValidation}
                  allowCopyRegistrantInfo={
                    isNacStyleAnswerType(question.answer_type)
                      ? ((question as { allow_copy_registrant_info?: boolean })
                          .allow_copy_registrant_info ?? true)
                      : undefined
                  }
                  {...companyCopyFieldProps({
                    question,
                    questions: registrationQuestions,
                    answers: answers as Record<string, unknown>,
                    registrantAccountId: data.registrant_account?.id,
                    excludeLicenseId: data.id,
                  })}
                />
              </div>
            );
          })}

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={Object.keys(validationErrors).length > 0}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
